/**
 * Roamer logic — pure functions for roam mode's visible wild spawns
 * (rung 1 of the diegetic ladder: presence). No Phaser. Unit-tested in
 * test/roamer-logic.test.ts; the Phaser-coupled RoamerSystem wrapper
 * lives in render/roamers.ts — same pure/wrapper split as
 * followerState.ts / follower.ts.
 *
 * Determinism contract: derivePopulation is a pure function of
 * (patches, rng) with a FIXED draw shape — per spawn, one tile draw
 * then pickEncounter's two draws (species, level). Fed by
 * seedStore.spawnRngFor(region, visit) — a derived stream keyed off
 * (master, region, visit) — the same visit always yields the same
 * cast, so a mid-visit reload can't re-roll the patch. The 'encounter'
 * domain is never touched: classic mode's byte-pinned CI gate has zero
 * surface here.
 */
import { ENCOUNTER_TABLES, pickEncounter } from '../data/encounters';

export interface GrassPatch {
  /** Stable id — index in flood-fill discovery order (row-major scan). */
  id: number;
  /** Member tiles as full-grid row-major indices (y * width + x). */
  tiles: number[];
}

export interface RoamerSpawn {
  species: string;
  level: number;
  /** Spawn tile in full-grid (global) coords. */
  x: number;
  y: number;
  patchId: number;
}

/** One roamer per ~10 patch tiles… */
export const ROAMER_PATCH_DIVISOR = 10;
/** …capped per patch (Let's Go-scale small: legibility, not perf)… */
export const ROAMER_PATCH_CAP = 3;
/** …and capped per region across all its patches. */
export const ROAMER_REGION_CAP = 8;

/**
 * Connected components of tall-grass tiles inside a region AABB
 * (4-connectivity, iterative flood fill, row-major discovery order so
 * patch ids + member order are deterministic for a given grid).
 *
 * `isGrass` abstracts the behavior-byte check (MB_TALL_GRASS) so this
 * module stays free of map-format constants; `width` is the FULL grid
 * stride (mega-map worldWidth) so returned indices match
 * mapData.behaviors indexing.
 */
export function findGrassPatches(
  isGrass: (x: number, y: number) => boolean,
  aabb: { x: number; y: number; w: number; h: number },
  width: number,
): GrassPatch[] {
  const seen = new Set<number>();
  const patches: GrassPatch[] = [];
  const x1 = aabb.x + aabb.w;
  const y1 = aabb.y + aabb.h;
  for (let y = aabb.y; y < y1; y++) {
    for (let x = aabb.x; x < x1; x++) {
      const idx = y * width + x;
      if (seen.has(idx) || !isGrass(x, y)) continue;
      // Flood fill from (x, y). Stack-based; push order (E, W, S, N)
      // is arbitrary but fixed — determinism comes from the fill
      // visiting a stable tile set, and member order only needs to be
      // reproducible, not meaningful.
      const tiles: number[] = [];
      const stack: Array<[number, number]> = [[x, y]];
      seen.add(idx);
      while (stack.length > 0) {
        const [cx, cy] = stack.pop()!;
        tiles.push(cy * width + cx);
        const neighbors: Array<[number, number]> = [
          [cx + 1, cy],
          [cx - 1, cy],
          [cx, cy + 1],
          [cx, cy - 1],
        ];
        for (const [nx, ny] of neighbors) {
          if (nx < aabb.x || nx >= x1 || ny < aabb.y || ny >= y1) continue;
          const nIdx = ny * width + nx;
          if (seen.has(nIdx) || !isGrass(nx, ny)) continue;
          seen.add(nIdx);
          stack.push([nx, ny]);
        }
      }
      patches.push({ id: patches.length, tiles });
    }
  }
  return patches;
}

/**
 * Derive a region's roamer cast for one visit. Pure: same
 * (regionName, patches, rng-stream) → same cast, byte for byte.
 *
 * Draw shape per spawn (fixed, so the stream never desyncs):
 *   1 draw  — tile pick (index into the patch's remaining candidates)
 *   2 draws — pickEncounter (weighted species, level within band)
 *
 * Species/level distribution is EXACTLY classic's: the same
 * ENCOUNTER_TABLES weights via the same pickEncounter, just consumed
 * from the derived spawn stream instead of the 'encounter' domain.
 * Regions without a table (towns) return [] up front, before ANY draw
 * — the guard is here (not only in the WorldScene gate) so the
 * documented draw shape is true unconditionally.
 */
export function derivePopulation(
  regionName: string,
  patches: GrassPatch[],
  rng: () => number,
  width: number,
): RoamerSpawn[] {
  if (!ENCOUNTER_TABLES[regionName]?.entries.length) return [];
  const spawns: RoamerSpawn[] = [];
  for (const patch of patches) {
    if (spawns.length >= ROAMER_REGION_CAP) break;
    const count = Math.min(
      Math.max(1, Math.floor(patch.tiles.length / ROAMER_PATCH_DIVISOR)),
      ROAMER_PATCH_CAP,
      ROAMER_REGION_CAP - spawns.length,
    );
    const candidates = [...patch.tiles];
    for (let i = 0; i < count && candidates.length > 0; i++) {
      const tileIdx = Math.floor(rng() * candidates.length);
      const tile = candidates.splice(tileIdx, 1)[0]!;
      const encounter = pickEncounter(regionName, rng);
      // Unreachable with the head guard (a non-empty table always
      // yields an entry) — defensive skip, never a mid-loop wipe of
      // spawns already accumulated.
      if (!encounter) break;
      spawns.push({
        species: encounter.species,
        // pickEncounter packs the rolled level into levelMin/levelMax.
        level: encounter.levelMin,
        x: tile % width,
        y: Math.floor(tile / width),
        patchId: patch.id,
      });
    }
  }
  return spawns;
}

/**
 * Pick a wander step for a roamer: one rng draw over the legal
 * cardinal neighbors (inside the roamer's home patch — the
 * movement-restriction guarantee that the player can always escape —
 * and not blocked by terrain/entities). Returns null when boxed in
 * (the roamer just idles this beat).
 */
export function pickWanderStep(
  rng: () => number,
  cur: { x: number; y: number },
  patchTiles: ReadonlySet<number>,
  isBlocked: (x: number, y: number) => boolean,
  width: number,
): { x: number; y: number; dir: 'up' | 'down' | 'left' | 'right' } | null {
  const options: Array<{ x: number; y: number; dir: 'up' | 'down' | 'left' | 'right' }> = [
    { x: cur.x, y: cur.y - 1, dir: 'up' },
    { x: cur.x, y: cur.y + 1, dir: 'down' },
    { x: cur.x - 1, y: cur.y, dir: 'left' },
    { x: cur.x + 1, y: cur.y, dir: 'right' },
  ];
  const legal = options.filter(
    (o) => patchTiles.has(o.y * width + o.x) && !isBlocked(o.x, o.y),
  );
  if (legal.length === 0) return null;
  return legal[Math.floor(rng() * legal.length)]!;
}
